// TODO: cleanup with research_assistant_gen_3 ff
//
// OR-6123 (Batch A) — pick the welcome greeting variant for an empty chat.
//
// Variant rule: "Hi!" when the project has no OTHER live chat besides the
// one currently being viewed/created; "Hi again!" once it has at least one.
// Deleting every chat resets the project back to "Hi!" — the greeting
// tracks what's actually there today, not whether a chat ever existed
// historically.

import type { WelcomeVariant } from '@verantos-dev/feature-epi-chat';

import { useProjectChatsQuery } from '../lib/api/useProjectChats';

export interface UseWelcomeVariantResult {
  /** The greeting variant to render once the chats list has loaded. */
  variant: WelcomeVariant;
  /**
   * False until the chats list has resolved. Callers should hold the
   * greeting render until this is true so the user never sees a
   * "Hi!" → "Hi again!" flicker as the list settles.
   */
  isReady: boolean;
}

/**
 * Resolve the welcome greeting variant for a project's empty chat.
 *
 * Reads the ``me`` chats query's live list, excluding ``currentChatId``:
 * no other chat → ``first`` ("Hi!"); at least one other chat → ``returning``
 * ("Hi again!").
 *
 * Excluding the current chat matters the instant a brand-new chat is
 * created: `useCreateChatMutation` optimistically writes the new row into
 * this same "me" list cache before its POST confirms, so by the time the
 * active chat view mounts, the list already contains the very chat being
 * greeted. Without excluding it, a project's first-ever chat would
 * immediately read as "returning" once the user sent their first message.
 */
export const useWelcomeVariant = (
  projectId: string,
  currentChatId?: string
): UseWelcomeVariantResult => {
  const { data, isLoading } = useProjectChatsQuery({ projectId, ownerFilter: 'me' });

  // Hold the greeting until the list resolves — default to the safer,
  // less-presumptuous "returning" copy for any transient render; callers
  // gate on `isReady` so this default is never actually shown.
  if (isLoading || data === undefined) {
    return { variant: 'returning', isReady: false };
  }

  const hasOtherChat = data.data.some((chat) => chat.id !== currentChatId);
  return { variant: hasOtherChat ? 'returning' : 'first', isReady: true };
};
